import { useState, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import "../InvoicesManagement.scss";
import InformationBox from "../../components/InformationBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";

import AddEntitlementsModal from "./AddEntitlementsModal";
import PageWrapper from "../../components/PageWrapper";
import ErrorBox from "../../components/ErrorBox";
import CustomButton from "../../components/CustomButton";
import addIconLight from "../../assets/add_icon_light.png";
import editIcon from "../../assets/edit_icon.png";
import UpdateEntitlementsModal from "./UpdateEntitlementsModal";
import editIconLight from "../../assets/edit_icon.png";
import axiosClient from "../../services/axiosClient";

const EntitlementsPage = () => {
  const { t } = useTranslation("general");
  const [isFetchingEntitlements, setIsFetchingEntitlements] = useState(false);
  const [fetchingEntitlementsError, setFetchingEntitlementsError] =
    useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [entitlements, setEntitlements] = useState([]);
  const [showEntitlementsModal, setShowEntitlementsModal] = useState(false);
  const [showEntitlementsEditModal, setShowEntitlementsEditModal] =
    useState(false);
  const [selectedEntitlement, setSelectedEntitlement] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const tableTitles = [
    { value: "name", keepOnMobile: true },
    { value: "price", keepOnMobile: true },
    { value: "notes", keepOnMobile: true },
    { value: "theOptions", keepOnMobile: true },
  ];

  const tableRows = entitlements.map((entitlement) => [
    {
      value: entitlement.name,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: entitlement.price,
      type: TABLE_CELL_TYPES.MONEY,
      keepOnMobile: true,
      currency: "IQD",
    },
    {
      value: entitlement.notes || t("noNotes"),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          title={t("edit")}
          icon={editIcon}
          iconSize={20}
          onClick={() => {
            setSelectedEntitlement(entitlement);
            setShowEntitlementsEditModal(true);
          }}
          variant={"blank"}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  // Data fetching
  const getAllEntitlementsData = async () => {
    try {
      setIsFetchingEntitlements(true);
      const response = await axiosClient.get("/entitlements/getEntitlements", {
        params: {
          page: pagination.currentPage,
          limit: 10,
          searchQuery,
        },
      });

      const entitlements = response.data.entitlements;

      setEntitlements(entitlements);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalEntitlements,
      });
    } catch (error) {
      console.error(
        "Error fetching entitlements:",
        error.response?.data || error
      );
      setFetchingEntitlementsError("errorFetchingEntitlements");
    } finally {
      setIsFetchingEntitlements(false);
    }
  };

  // Effects
  useEffect(() => {
    getAllEntitlementsData();
  }, [pagination.currentPage]);

  return (
    <PageWrapper title={t("entitlements")} withBackButton>
      <div className="u-margin--top_700" />

      <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-margin--bottom_400">
        <CustomButton
          title={t("addEntitlement")}
          onClick={() => setShowEntitlementsModal(true)}
          icon={addIconLight}
          iconSize={20}
        />
      </div>
      {fetchingEntitlementsError && (
        <ErrorBox>{t(fetchingEntitlementsError)}</ErrorBox>
      )}
      {isFetchingEntitlements && <Loader size={LOADER_SIZES.MEDIUM} />}

      {!fetchingEntitlementsError &&
        !isFetchingEntitlements &&
        entitlements.length === 0 && (
          <>
            <InformationBox>{t("noEntitlementsFound")}</InformationBox>
          </>
        )}

      {!fetchingEntitlementsError &&
        !isFetchingEntitlements &&
        entitlements.length > 0 && (
          <>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          </>
        )}
      {showEntitlementsModal && (
        <AddEntitlementsModal
          onClose={() => setShowEntitlementsModal(false)}
          onSuccess={() => {
            setShowEntitlementsModal(false);
            getAllEntitlementsData();
          }}
        />
      )}

      {showEntitlementsEditModal && (
        <UpdateEntitlementsModal
          entitlement={selectedEntitlement}
          onClose={() => setShowEntitlementsEditModal(false)}
          onSuccess={() => {
            setShowEntitlementsEditModal(false);
            getAllEntitlementsData();
          }}
        />
      )}
    </PageWrapper>
  );
};

export default EntitlementsPage;
